import React, { useMemo } from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { Loader2 } from 'lucide-react';
import type { Location } from '../types';
import {
  getLocationDisplayName,
  isLocationActive,
  ACTIVE_LOCATION_COLOR,
  INACTIVE_LOCATION_COLOR,
} from '../utils/locationDisplay';

export type LocationSelectOption = {
  value: string;
  label: string;
  isInactive: boolean;
};

const selectStyles: StylesConfig<LocationSelectOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: '0.875rem',
    borderColor: state.isFocused ? '#3b82f6' : '#d4d4d4',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: '#a3a3a3' },
    backgroundColor: state.isDisabled ? '#f5f5f5' : '#fff',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    color: state.data.isInactive ? INACTIVE_LOCATION_COLOR : ACTIVE_LOCATION_COLOR,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#f5f5f5' : '#fff',
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.data.isInactive ? INACTIVE_LOCATION_COLOR : ACTIVE_LOCATION_COLOR,
  }),
};

interface LocationSelectProps {
  locations: Location[];
  value: number | null;
  onChange: (locationId: number | null) => void;
  disabled?: boolean;
  loading?: boolean;
  refreshing?: boolean;
  allowAllLocations?: boolean;
  className?: string;
  title?: string;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  locations,
  value,
  onChange,
  disabled = false,
  loading = false,
  refreshing = false,
  allowAllLocations = true,
  className = '',
  title,
}) => {
  const options = useMemo(() => {
    const rows: LocationSelectOption[] = allowAllLocations
      ? [{ value: '', label: 'All Locations', isInactive: false }]
      : [];
    for (const loc of locations) {
      rows.push({
        value: String(loc.id),
        label: getLocationDisplayName(loc),
        isInactive: !isLocationActive(loc),
      });
    }
    // Keep the current selection visible while the list refreshes in the background.
    if (
      value != null &&
      !rows.some((row) => row.value === String(value))
    ) {
      rows.push({
        value: String(value),
        label: `Location #${value}`,
        isInactive: false,
      });
    }
    return rows;
  }, [locations, allowAllLocations, value]);

  const selectedOption = useMemo(() => {
    if (value == null) {
      return options.find((o) => o.value === '') ?? null;
    }
    return options.find((o) => o.value === String(value)) ?? null;
  }, [options, value]);

  const handleChange = (opt: SingleValue<LocationSelectOption>) => {
    if (!opt || opt.value === '') {
      onChange(null);
      return;
    }
    onChange(Number(opt.value));
  };

  const hasKnownSelection = value != null;
  const isDisabled = disabled || (loading && locations.length === 0 && !hasKnownSelection);
  const placeholder =
    loading && locations.length === 0 && !hasKnownSelection
      ? 'Loading locations…'
      : locations.length === 0
        ? 'No locations assigned'
        : 'Select location…';

  return (
    <div className={`flex items-center gap-1.5 ${className}`} title={title}>
      <div className="w-52 min-w-[12rem]">
        <Select<LocationSelectOption, false>
          inputId="header-location-select"
          options={options}
          value={selectedOption}
          onChange={handleChange}
          isDisabled={isDisabled}
          isSearchable={locations.length > 6}
          isLoading={loading && locations.length === 0 && !hasKnownSelection}
          placeholder={placeholder}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          styles={selectStyles}
          classNamePrefix="location-select"
          aria-label="Workplace location"
        />
      </div>
      {refreshing && (
        <Loader2 className="h-4 w-4 text-neutral-400 animate-spin flex-shrink-0" aria-hidden />
      )}
    </div>
  );
};

export default LocationSelect;
